// @oasis-permission-inherits: /creator-styles (ai-edit-v2-tool:view)
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAuthStore } from "~/modules/auth/auth-store";
import {
  importCreatorStyleMutation,
  listCreatorStylesQuery,
} from "~/modules/creator-styles/queries";
import {
  creatorStyleSortSchema,
  creatorStyleVisibilitySchema,
} from "~/modules/creator-styles/schemas";

const visibilityFilterSchema = z.union([
  creatorStyleVisibilitySchema,
  z.literal("active"),
  z.literal("all"),
]);

export const Route = createFileRoute("/_dash/creator-styles/")({
  validateSearch: z.object({
    q: z.string().optional().catch(undefined),
    visibility: visibilityFilterSchema.default("active"),
    sort: creatorStyleSortSchema.default("rank"),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [user] = useAuthStore((state) => [state.user]);
  const canWrite = user?.permissions.includes("ai-edit-v2-tool:write") ?? false;
  const [source, setSource] = useState("");
  const [displayName, setDisplayName] = useState("");

  const params = {
    ...(search.q?.trim() ? { q: search.q.trim() } : {}),
    ...(search.visibility !== "active" && search.visibility !== "all"
      ? { visibility: search.visibility }
      : {}),
    sort: search.sort,
  };
  const { data, error, isLoading } = listCreatorStylesQuery.useQuery({ params });
  const styles =
    search.visibility === "active"
      ? data?.styles.filter(({ style }) => style.availability.visibility !== "archived")
      : data?.styles;
  const importStyle = importCreatorStyleMutation.useMutation({
    onSuccess(response) {
      toast.success("Creator style imported");
      setSource("");
      setDisplayName("");
      void navigate({
        to: "/creator-styles/$styleId",
        params: { styleId: response.style.videoStyleId },
      });
    },
    onError(importError) {
      toast.error(importError instanceof Error ? importError.message : "Import failed");
    },
  });

  function updateSearch(next: Partial<typeof search>) {
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true });
  }

  function handleImport(event: FormEvent) {
    event.preventDefault();
    if (!source.trim()) {
      return;
    }
    importStyle.mutate({
      params: {
        source: source.trim(),
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      },
    });
  }

  return (
    <main className="space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold">Creator Styles</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Import finished creator projects and manage their FX style catalog entries.
        </p>
      </div>

      {canWrite && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plus className="size-4" /> Import a Captions project
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleImport} className="grid gap-4 lg:grid-cols-2">
              <Field label="Captions project ID or URL" required>
                <Input
                  value={source}
                  onChange={(event) => setSource(event.target.value)}
                  placeholder="captions.ai/videos/tHrIH7wkEvNbX9MFVIe8"
                  disabled={importStyle.isPending}
                  required
                />
              </Field>
              <Field label="Style name">
                <Input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Uses the project title when empty"
                  disabled={importStyle.isPending}
                />
              </Field>
              <div className="lg:col-span-2">
                <Button type="submit" disabled={importStyle.isPending}>
                  {importStyle.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Start import
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search.q ?? ""}
            onChange={(event) => updateSearch({ q: event.target.value || undefined })}
            className="pl-9"
            placeholder="Search names, creators, and categories"
          />
        </div>
        <Select
          value={search.visibility}
          onValueChange={(value) =>
            updateSearch({ visibility: visibilityFilterSchema.parse(value) })
          }
        >
          <SelectTrigger className="w-40 capitalize">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["active", "all", "draft", "internal", "public", "archived"] as const).map(
              (value) => (
                <SelectItem key={value} value={value} className="capitalize">
                  {value}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
        <Select
          value={search.sort}
          onValueChange={(value) => updateSearch({ sort: creatorStyleSortSchema.parse(value) })}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rank">Catalog order</SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading creator styles...</p>}
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 300px))",
        }}
      >
        {styles?.map(({ style, thumbnailUrl, previewVideoUrl }) => (
          <Link
            key={style.videoStyleId}
            to="/creator-styles/$styleId"
            params={{ styleId: style.videoStyleId }}
            search={search}
            className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Card className="h-full gap-0 overflow-hidden py-0 transition-colors hover:border-foreground/30">
              <div className="aspect-video bg-muted">
                <CreatorStyleCardPreview
                  thumbnailUrl={thumbnailUrl}
                  previewVideoUrl={previewVideoUrl}
                />
              </div>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{style.displayName}</div>
                    <div className="text-sm text-muted-foreground">{style.creator.displayName}</div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {style.availability.visibility}
                  </Badge>
                </div>
                <div className="flex gap-2 text-xs text-muted-foreground">
                  <span>Rank {style.rank}</span>
                  <span>·</span>
                  <span className="capitalize">{style.processingStatus}</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      {!isLoading && styles?.length === 0 && (
        <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
          No creator styles found.
        </div>
      )}
    </main>
  );
}

function CreatorStyleCardPreview({
  thumbnailUrl,
  previewVideoUrl,
}: {
  thumbnailUrl?: string | null;
  previewVideoUrl?: string | null;
}) {
  if (thumbnailUrl) {
    return <img src={thumbnailUrl} alt="" className="size-full object-cover" />;
  }
  if (previewVideoUrl) {
    return <video src={previewVideoUrl} muted className="size-full object-cover" />;
  }
  return null;
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="space-y-2">
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      {children}
    </label>
  );
}
